/* eslint-disable camelcase */

import _ from 'lodash';

/**
 * Computes the arithmetic mean of a sample series. Sums the values and divides by the element count.
 *
 * @param array - Samples to average.
 * @returns The mean of the samples.
 */
export const getAverage = function (array: number[]) {
  return _.sum(array) / array.length;
};

/**
 * Takes a bounded slice of a sample buffer without running past its end. Slices count elements from the start index, or everything remaining when the requested run would overflow the buffer.
 *
 * @param array - Buffer to slice.
 * @param startIdx - Index to start the slice at.
 * @param count - Maximum number of samples to take.
 * @returns The requested samples, truncated at the end of the buffer.
 */
export const getRange = function (array: number[], startIdx: number, count: number) {
  let result = [];
  if (startIdx + count > array.length) {
    result = _.slice(array, startIdx, array.length);
  } else {
    result = _.slice(array, startIdx, startIdx + count);
  }
  return result;
};

/**
 * Appends every element of one sample buffer onto another during Pan-Tompkins beat detection. Pushes the elements one at a time in a forEach rather than spreading them, because the spread form overflows on large ECG buffers in Safari.
 *
 * @param arrayDes - Buffer appended to, mutated in place.
 * @param arrayIn - Samples to append.
 * @returns Nothing.
 */
export const addRange = function (arrayDes: number[], arrayIn: number[]) {
  // arrayDes.push(...arrayIn); // *: This method cause error in Safari
  arrayIn.forEach((e) => arrayDes.push(e));
};

/**
 * Copies a run of samples from one buffer into another at a given offset. Logs an out-of-range warning when either buffer is too short but copies anyway, then writes count elements element by element from the source position into the destination position.
 *
 * @param source - Buffer read from.
 * @param sourcePos - Index in the source to start reading at.
 * @param des - Buffer written into, mutated in place.
 * @param desPos - Index in the destination to start writing at.
 * @param count - Number of samples to copy.
 * @returns Nothing.
 */
export const copyArray = function (
  source: number[],
  sourcePos: number,
  des: number[],
  desPos: number,
  count: number,
) {
  if (des.length < desPos + count || source.length < sourcePos + count) {
    console.log('Error! Unexpected input: out of range');
  }
  let j = sourcePos;
  for (let i = desPos; i < desPos + count; i += 1) {
    des[i] = source[j];
    j += 1;
  }
};

/**
 * Band-passes an ECG channel to isolate the QRS energy band before peak detection. Runs a hardcoded order-10 IIR recurrence over the samples, dividing each input by the filter gain and carrying ten delayed input and output taps, then zeroes the taps and runs the same recurrence backwards over the result so the stage adds no phase shift.
 *
 * @param data - Raw ECG samples for one channel.
 * @returns The band-passed samples.
 */
export const Butterworth_bandpass = function (data: number[]) {
  const dataLength = data.length;
  const filt_data = new Array(dataLength);
  const Nzeros = 10;
  const Npoles = 10;
  const GAIN = 4.673735821e4;
  const xv = new Array(Nzeros + 1).fill(0);
  const yv = new Array(Npoles + 1).fill(0);
  for (let i = 0; i < dataLength; i += 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] =
      xv[10] -
      xv[0] +
      5 * (xv[2] - xv[8]) +
      10 * (xv[6] - xv[4]) +
      -0.4422091824 * yv[0] +
      4.6680654686 * yv[1] +
      -22.31324764 * yv[2] +
      63.59414794 * yv[3] +
      -119.67119788 * yv[4] +
      155.35936094 * yv[5] +
      -140.91060266 * yv[6] +
      88.167268075 * yv[7] +
      -36.42097566 * yv[8] +
      8.9693904453 * yv[9];
    filt_data[i] = yv[10];
  }
  xv.fill(0);
  yv.fill(0);
  const back_filt_data = new Array(dataLength);
  for (let i = filt_data.length - 1; i > -1; i -= 1) {
    xv[0] = xv[1];
    xv[1] = xv[2];
    xv[2] = xv[3];
    xv[3] = xv[4];
    xv[4] = xv[5];
    xv[5] = xv[6];
    xv[6] = xv[7];
    xv[7] = xv[8];
    xv[8] = xv[9];
    xv[9] = xv[10];
    xv[10] = filt_data[i] / GAIN;
    yv[0] = yv[1];
    yv[1] = yv[2];
    yv[2] = yv[3];
    yv[3] = yv[4];
    yv[4] = yv[5];
    yv[5] = yv[6];
    yv[6] = yv[7];
    yv[7] = yv[8];
    yv[8] = yv[9];
    yv[9] = yv[10];
    yv[10] =
      xv[10] -
      xv[0] +
      5 * (xv[2] - xv[8]) +
      10 * (xv[6] - xv[4]) +
      -0.4422091824 * yv[0] +
      4.6680654686 * yv[1] +
      -22.31324764 * yv[2] +
      63.59414794 * yv[3] +
      -119.67119788 * yv[4] +
      155.35936094 * yv[5] +
      -140.91060266 * yv[6] +
      88.167268075 * yv[7] +
      -36.42097566 * yv[8] +
      8.9693904453 * yv[9];
    back_filt_data[i] = yv[10];
  }
  return back_filt_data;
};

/**
 * Computes the first derivative of an ECG segment for QRS slope analysis. Uses a forward difference at the first index and a backward difference at the last, and averages the backward and forward differences at each interior index up to nx minus two.
 *
 * @param data - ECG samples to differentiate.
 * @param nx - Number of samples to process and length of the result.
 * @returns The per-sample first derivative of the segment.
 */
export const FirstDerivative = function (data: number[], nx: number) {
  let i;
  let tmp1;
  let tmp2;
  const firstderv = new Array(nx);
  // *: Compute derivative first index
  firstderv[0] = data[1] - data[0];

  for (i = 1; i < nx - 2; i += 1) {
    tmp1 = data[i] - data[i - 1];
    tmp2 = data[i + 1] - data[i];
    firstderv[i] = (tmp2 + tmp1) / 2;
  }

  // *: Compute derivative last index
  firstderv[nx - 1] = data[nx - 1] - data[nx - 2];
  return firstderv;
};

/**
 * Squares an ECG series in place, the energy stage of Pan-Tompkins beat detection. Multiplies each element by itself, mutating and returning the caller's array rather than a copy.
 *
 * @param ecgArrIn - Samples to square, mutated in place.
 * @returns The same array holding the squared samples.
 */
export const Pow_list = function (ecgArrIn: number[]) {
  const ecgArr = ecgArrIn;
  const ecgArrLength = ecgArr.length;
  for (let i = 0; i < ecgArrLength; i += 1) {
    ecgArr[i] *= ecgArr[i];
  }
  return ecgArr;
};

/**
 * Picks locally maximal peaks from an integrated ECG signal while enforcing a refractory gap. Walks the samples and accepts an index when it is the maximum of the window spanning the radius around it, then jumps a whole radius ahead so two peaks are never closer than that distance, with the leading samples handled by a shorter window.
 *
 * @param sig - Integrated ECG samples to scan for peaks.
 * @param threshold - Amplitude threshold; not consulted by the current implementation.
 * @param distance - Minimum sample gap enforced between accepted peaks.
 * @returns The sample indices of the accepted peaks, in ascending order.
 */
export const Find_peaks = function (sig: number[], threshold: number, distance: number) {
  const peaks = [];
  let i = 0;
  const radius = parseInt(String(distance), 10);
  while (i < radius + 1) {
    if (sig[i] === _.max(getRange(sig, 0, i + radius))) {
      peaks.push(i);
      i += radius;
    } else {
      i += 1;
    }
  }
  while (i < sig.length) {
    if (
      sig[i] === _.max(getRange(sig, i - radius, _.min([radius * 2, sig.length - (i - radius)])))
    ) {
      peaks.push(i);
      i += radius;
    } else {
      i += 1;
    }
  }
  // while (i < sig.length) {
  //   if (sig[i] === _.max(getRange(sig, i - radius, sig.length - (i - radius)))) {
  //     peaks.push(i);
  //     i += radius;
  //   } else {
  //     i += 1;
  //   }
  // }
  return peaks;
};

/**
 * Reads the ECG amplitudes sitting at a set of detected peak locations. Walks the location list, coerces each entry to an integer index and collects the sample found there.
 *
 * @param ecg - ECG samples to read from.
 * @param locs - Sample indices to read.
 * @returns The amplitudes at the requested locations, in the order given.
 */
export const List_from_list = function (ecg: number[], locs: number[]) {
  const new_list = [];
  for (let i = 0; i < locs.length; i += 1) {
    new_list.push(ecg[parseInt(String(locs[i]), 10)]);
  }
  return new_list;
};

/**
 * Locates the peak amplitude in an ECG window and every index that attains it. Finds the maximum value, collects all indices equal to it and the first such index, returning zeros and an empty index list for an empty window.
 *
 * @param ecg_array - ECG samples to scan for a maximum.
 * @returns An object carrying y_i, the peak value, i_l, every index reaching it, and x_i, the first such index.
 */
export const Find_max_locate = function (ecg_array: number[]) {
  let y_i: number | undefined;
  let i_l: number[];
  let x_i: number;
  if (ecg_array.length > 0) {
    y_i = _.max(ecg_array);
    i_l = _.range(ecg_array.length).filter((x) => ecg_array[x] === y_i);
    x_i = ecg_array.findIndex((x) => x === y_i);
  } else {
    y_i = 0;
    i_l = [];
    x_i = 0;
  }
  return { y_i, i_l, x_i };
};

/**
 * Takes the first difference of a sample series, the slope stage of Pan-Tompkins beat detection. Walks the array building a new one from each element subtracted from its successor, so the result is one element shorter.
 *
 * @param array - Samples to differentiate.
 * @returns The successive differences between adjacent samples.
 */
export const Diff = function (array: number[]) {
  // return _.zipWith(array, _.slice(array, 1, array.length), (a, b) => (b - a));
  const newArr = [];
  for (let i = 0; i < array.length - 1; i += 1) {
    newArr.push(array[i + 1] - array[i]);
  }
  return newArr;
};

const compare_array = new Int16Array([
  -819, -813, -807, -801, -795, -788, -782, -776, -770, -764, -758, -752, -746, -740, -734, -729,
  -723, -717, -712, -706, -701, -695, -690, -685, -680, -675, -670, -665, -660, -656, -651, -647,
  -642, -638, -634, -630, -626, -622, -619, -615, -612, -608, -605, -602, -600, -597, -594, -592,
  -590, -587, -585, -584, -582, -580, -579, -578, -577, -576, -575, -574, -574, -574, -573, -573,
  -574, -574, -574, -575, -576, -577, -578, -579, -580, -582, -584, -585, -587, -590, -592, -594,
  -597, -600, -602, -605, -608, -612, -615, -619, -622, -626, -630, -634, -638, -642, -647, -651,
  -656, -660, -665, -670, -675, -680, -685, -690, -695, -701, -706, -712, -717, -723, -729, -734,
  -740, -746, -752, -758, -764, -770, -776, -782, -788, -795, -801, -807, -813, 819, 813, 807, 801,
  795, 788, 782, 776, 770, 764, 758, 752, 746, 740, 734, 729, 723, 717, 712, 706, 701, 695, 690,
  685, 680, 675, 670, 665, 660, 656, 651, 647, 642, 638, 634, 630, 626, 622, 619, 615, 612, 608,
  605, 602, 600, 597, 594, 592, 590, 587, 585, 584, 582, 580, 579, 578, 577, 576, 575, 574, 574,
  574, 573, 573, 574, 574, 574, 575, 576, 577, 578, 579, 580, 582, 584, 585, 587, 590, 592, 594,
  597, 600, 602, 605, 608, 612, 615, 619, 622, 626, 630, 634, 638, 642, 647, 651, 656, 660, 665,
  670, 675, 680, 685, 690, 695, 701, 706, 712, 717, 723, 729, 734, 740, 746, 752, 758, 764, 770,
  776, 782, 788, 795, 801, 807, 813,
]);

/**
 * Converts raw ADC counts from the recorder back into millivolts. Returns nothing when the gain is zero, otherwise divides each sample by the gain and substitutes the maximum float for any non-numeric result, while tracking runs matching the lead-off marker sequence and zeroing each completed run in the output.
 *
 * @param ecg - Raw ECG sample counts from the device.
 * @param gain - ADC counts per millivolt; a zero gain yields an empty result.
 * @returns The samples converted to millivolts, with marker runs flattened to zero.
 */
export const Return_real_value = function (ecg: number[], gain: number) {
  const ecg_output = [];
  if (gain !== 0) {
    let i_compare = 0;
    for (let i = 0; i < ecg.length; i += 1) {
      if (ecg[i] === compare_array[i_compare]) {
        i_compare += 1;
      } else {
        i_compare = 0;
      }

      if (!_.isNaN(ecg[i] / gain)) {
        ecg_output.push(ecg[i] / gain);
      } else {
        ecg_output.push(Number.MAX_VALUE);
      }

      if (i_compare === compare_array.length) {
        for (let j = 0; j < compare_array.length; j += 1) {
          ecg_output[i - j] = 0;
        }
        i_compare = 0;
      }
    }
  }
  return ecg_output;
};

/**
 * Drops detected peak locations whose amplitude looks like noise rather than a QRS complex. Keeps only locations whose amplitude magnitude is under 4 and whose non-negativity test passes, falling back to the original locations only when the filter itself yields nothing.
 *
 * @param locs - Candidate peak sample indices.
 * @param new_ecg - Processed ECG samples the amplitudes are read from.
 * @returns The peak locations that survived the amplitude checks.
 */
const Remove_low_peak = function (locs: number[], new_ecg: number[]) {
  const suitable_locs = locs.filter(
    (peak) => Math.abs(new_ecg[peak]) < 4 && Math.abs(Number(new_ecg[peak] >= 0.01)),
  );
  if (suitable_locs) {
    return suitable_locs;
  }
  return locs;
};

/**
 * Removes baseline wander from an ECG series before beat detection. Runs two cascaded moving averages sized to 0.2 and 0.6 seconds of samples, clipping each window at the ends of the record, then subtracts the doubly-smoothed baseline from the original samples and rounds each residual to four decimals.
 *
 * @param ecg - Raw ECG samples for one channel.
 * @param samplingRate - Samples per second, used to size both averaging windows.
 * @returns The baseline-corrected samples.
 */
const newBaseLineAndEnhancement = (ecg: number[], samplingRate: number) => {
  const N = ecg.length;

  const flen1 = Math.floor((samplingRate * 0.2) / 2);
  const flen2 = Math.floor((samplingRate * 0.6) / 2);

  // *: Baseline wander remove
  const b1 = new Array(N);
  const nor = new Array(N);
  const bwr = new Array(N);

  for (let j = 0; j < N; j += 1) {
    const start = Math.max(j - flen1, 0);
    const stop = Math.min(j + flen1, N);
    let mean = 0;

    for (let i = start; i < stop; i += 1) {
      mean += ecg[i];
    }

    b1[j] = mean / (stop - start);
  }

  for (let j = 0; j < N; j += 1) {
    const start = Math.max(j - flen2, 0);
    const stop = Math.min(j + flen2, N);
    let mean = 0;

    for (let i = start; i < stop; i += 1) {
      mean += b1[i];
    }

    nor[j] = mean / (stop - start);
    bwr[j] = parseFloat((ecg[j] - nor[j]).toFixed(4));
  }

  return bwr;
};

/**
 * Seeds the Pan-Tompkins signal and noise thresholds from the opening seconds of an ECG record. Takes a third of the peak and half the mean over the first two seconds of samples, and recomputes both from a copy with outliers beyond three times the mean magnitude discarded whenever that copy is non-empty.
 *
 * @param ecg - Integrated ECG samples the thresholds are seeded from.
 * @param samplingRate - Samples per second, used to size the two-second learning window.
 * @returns An object carrying thr_sig, the initial signal threshold, and thr_noise, the initial noise threshold.
 */
const Calculate_thr = function (ecg: number[], samplingRate: number) {
  const average = Math.abs(getAverage(ecg));
  let thr_sig = (_.max(getRange(ecg, 0, Math.min(2 * samplingRate, ecg.length))) * 1) / 3;
  let thr_noise = (getAverage(getRange(ecg, 0, Math.min(2 * samplingRate, ecg.length))) * 1) / 2;
  const filtered_ecg = ecg.filter((x) => Math.abs(x) < average * 3);
  if (filtered_ecg && filtered_ecg.length > 0) {
    thr_sig =
      (_.max(getRange(filtered_ecg, 0, Math.min(2 * samplingRate, filtered_ecg.length))) * 1) / 3;
    thr_noise =
      (getAverage(getRange(filtered_ecg, 0, Math.min(2 * samplingRate, filtered_ecg.length))) * 1) /
      2;
  }
  return { thr_sig, thr_noise };
};

/**
 * Smooths an ECG series with a Hanning-weighted moving average. Passes the input through untouched for a window under 3, rounds an even window up to the next odd size, builds and sums the Hanning coefficients, mirror-pads the series by half a window at each end and writes each output sample as the normalized weighted sum over that window.
 *
 * @param raw - Samples to smooth.
 * @param lenWindow - Requested window length; rounded up to odd, and under 3 disables smoothing.
 * @returns The smoothed samples, at the same length as the input.
 */
const RM_Smooth = (raw: number[], lenWindow: number) => {
  let updatedLenWindow = lenWindow;
  if (updatedLenWindow < 3) {
    return raw;
  }
  if (updatedLenWindow % 2 === 0) {
    updatedLenWindow += 1;
  }
  // *: Create hanning window
  const hanning = new Array(updatedLenWindow);
  let SumWindow = 0;
  for (let i = 0; i < hanning.length; i += 1) {
    hanning[i] = 0.5 - 0.5 * Math.cos((2.0 * Math.PI * i) / (updatedLenWindow - 1));
    SumWindow += hanning[i];
  }
  const temp = new Array(raw.length + (updatedLenWindow - 1));
  const edge = (updatedLenWindow - 1) / 2;

  // *: Padding
  for (let i = 0; i < edge; i += 1) {
    temp[i] = raw[edge - i];
    temp[temp.length - 1 - i] = raw[raw.length - 1 - edge + i];
  }
  for (let i = 0; i < raw.length; i += 1) {
    temp[edge + i] = raw[i];
  }
  const out_ = new Array(raw.length);

  for (let i = 0; i < temp.length - (updatedLenWindow - 1); i += 1) {
    let sum = 0;
    for (let j = 0; j < updatedLenWindow; j += 1) {
      sum += (hanning[j] * temp[i + j]) / SumWindow;
    }
    out_[i] = sum;
  }
  return out_;
};

/**
 * Clamps an ECG working buffer into a permitted amplitude range in place. Raises every element to the lower bound, and applies the upper bound only when it is not the -10 sentinel that marks it as unset.
 *
 * @param array - Samples to clamp, mutated in place.
 * @param value_min - Lower bound applied to every sample.
 * @param value_max - Upper bound, or -10 to leave the samples uncapped above.
 * @returns The same array after clamping.
 */
const Clip = (array: number[], value_min: number, value_max = -10) => {
  for (let index = 0; index < array.length; index += 1) {
    array[index] = Math.max(value_min, array[index]);
    if (value_max !== -10) {
      array[index] = Math.min(value_max, array[index]);
    }
  }
  return array;
};

/**
 * Rescales a baseline-corrected ECG so amplitudes across the record are comparable. Builds a running local-maximum envelope of the absolute samples over the window, repeatedly zeroing out points above 5 until no such outliers remain, then smooths the envelope with the Hanning average, clamps it at a third of its own mean or 0.1, replaces near-zero entries with 1 and divides the samples in place by it.
 *
 * @param ecg_baseline - Baseline-corrected samples, mutated in place by the rescaling.
 * @param window_len - Window length in samples for the envelope and its smoothing.
 * @returns The same array holding the normalized samples.
 */
const Normalize = (ecg_baseline: number[], window_len: number) => {
  const ecg_normalize = ecg_baseline;
  const abs_raw = ecg_normalize.map((x) => Math.abs(x));
  const g = new Array(ecg_normalize.length);
  while (true) {
    for (let index = 0; index < abs_raw.length; index += 1) {
      const start_index = Math.max(0, index - parseInt(String(window_len / 2), 10));
      const stop_index = Math.min(abs_raw.length - 1, index + parseInt(String(window_len / 2), 10));
      const max_value = _.max(getRange(abs_raw, start_index, stop_index - start_index));
      g[index] = max_value;
    }
    if (_.max(abs_raw) < 5) {
      break;
    }
    const list_index = _.range(abs_raw.length).filter((x) => abs_raw[x] > 5);
    for (let index = 0; index < list_index.length; index += 1) {
      abs_raw[list_index[index]] = 0;
    }
  }
  let g_smooth = RM_Smooth(g, window_len);
  const g_mean = Math.max(getAverage(g_smooth) / 3.0, 0.1);
  g_smooth = Clip(g_smooth, g_mean);
  const smooth_index = _.range(ecg_baseline.length).filter((x) => g_smooth[x] < 0.01);
  if (smooth_index.length > 0) {
    smooth_index.forEach((index) => {
      g_smooth[index] = 1;
    });
  }
  for (let index = 0; index < ecg_normalize.length; index += 1) {
    ecg_normalize[index] /= g_smooth[index];
  }
  return ecg_normalize;
};

/**
 * Detects R-peak locations in an ECG channel with the Pan-Tompkins algorithm. Converts counts to millivolts, removes baseline wander, band-passes, normalizes and squares the trace, picks candidates at least 0.22 seconds apart, then walks them maintaining adaptive signal and noise levels, halving the thresholds when the running RR interval destabilizes, searching back over intervals longer than 1.66 times the selected RR, rejecting T waves by slope comparison and dropping low-amplitude picks.
 *
 * @param samplingRate - Samples per second of the recording.
 * @param gain - ADC counts per millivolt used to scale the raw samples.
 * @param ecgData - Raw ECG samples for one channel; empty input yields no beats.
 * @returns The sample indices of the detected QRS complexes.
 */
export const getBeatMarkersArr = function (samplingRate: number, gain: number, ecgData: number[]) {
  if (!ecgData || ecgData.length === 0) {
    return [];
  }
  let ecg_m = [];

  let new_ecg = Return_real_value(ecgData, gain);
  new_ecg = newBaseLineAndEnhancement(new_ecg, samplingRate);
  let ecg_h = Butterworth_bandpass(new_ecg);
  ecg_h = Normalize(ecg_h, samplingRate * 0.6);

  ecg_m = Pow_list(ecg_h);
  let locs = Find_peaks(ecg_m, 0.00000000001, samplingRate * 0.22);

  locs = Remove_low_peak(locs, ecg_m);

  const pks = List_from_list(ecg_m, locs);

  let peak_index;
  const qrs_c = [];
  let qrs_i = [];
  const qrs_i_raw = [];
  const qrs_amp_raw = [];
  const noise_c = [];
  const noise_i = [];

  let x;
  let x1 = null;
  let skip = 0;
  let ser_back = 0;
  let y_i;
  let x_i;
  let i_l;

  let { thr_sig: THR_SIG, thr_noise: THR_NOISE } = Calculate_thr(ecg_m, samplingRate);
  let SIG_LEV = THR_SIG;
  let NOISE_LEV = THR_NOISE;

  let { thr_sig: THR_SIG1, thr_noise: THR_NOISE1 } = Calculate_thr(ecg_h, samplingRate);
  let SIG_LEV1 = THR_SIG1;
  let NOISE_LEV1 = THR_NOISE1;

  let m_selected_RR = 0;
  let meanRR = 0;

  for (peak_index = 0; peak_index < pks.length; peak_index += 1) {
    let ecg_array = null;
    ser_back = 0;
    if (
      locs[peak_index] - Math.round(0.15 * samplingRate) >= 1 &&
      locs[peak_index] <= ecg_h.length - 1
    ) {
      ecg_array = getRange(
        ecg_h,
        locs[peak_index] - Math.round(0.15 * samplingRate),
        Math.round(0.15 * samplingRate),
      );
      const result = Find_max_locate(ecg_array);
      y_i = result.y_i;
      i_l = result.i_l;
      x_i = result.x_i;
    } else {
      if (peak_index === 0) {
        ecg_array = getRange(ecg_h, 0, locs[peak_index]);
        ser_back = 1;
      } else if (locs[peak_index] >= ecg_h.length) {
        ecg_array = getRange(
          ecg_h,
          locs[peak_index] - Math.round(0.15 * samplingRate),
          ecg_h.length - (locs[peak_index] - Math.round(0.15 * samplingRate)),
        );
      }
      if (ecg_array !== null) {
        const result = Find_max_locate(ecg_array);
        y_i = result.y_i;
        i_l = result.i_l;
        x_i = result.x_i;
      } else {
        y_i = 0;
        i_l = [];
        x_i = 0;
      }
    }
    if (qrs_c !== null) {
      x = qrs_c.length;
    } else {
      x = 0;
    }
    if (x >= 9) {
      x1 = getRange(qrs_i, x - 9, 9);
      const diffRR = Diff(x1);
      meanRR = getAverage(diffRR);
      const comp = qrs_i[qrs_i.length - 1] - qrs_i[qrs_i.length - 2];
      if (comp <= 0.92 * meanRR || comp >= 1.16 * meanRR) {
        THR_SIG *= 0.5;
        THR_SIG1 *= 0.5;
      } else {
        m_selected_RR = meanRR;
      }
    }
    let test_m = -3000;
    if (m_selected_RR !== 0) {
      test_m = m_selected_RR;
    } else if (meanRR !== 0 && m_selected_RR === 0) {
      test_m = meanRR;
    } else {
      test_m = 0;
    }
    if (test_m !== 0) {
      if (locs[peak_index] - qrs_i[qrs_i.length - 1] >= Math.round(1.66 * test_m)) {
        const obj = Find_max_locate(
          getRange(
            ecg_m,
            qrs_i[qrs_i.length - 1] + Math.round(0.2 * samplingRate),
            locs[peak_index] -
              Math.round(0.2 * samplingRate) -
              (qrs_i[qrs_i.length - 1] + Math.round(0.2 * samplingRate)),
          ),
        );
        const pks_temp = obj.y_i;
        const i_3 = obj.i_l;
        let locs_temp = obj.x_i;
        locs_temp = qrs_i[qrs_i.length - 1] + Math.round(0.2 * samplingRate) + locs_temp - 1;
        if (pks_temp < getAverage(qrs_c) * 10) {
          if (pks_temp > 1.75 * THR_NOISE) {
            qrs_c.push(pks_temp);
            qrs_i.push(locs_temp);
          }
          let y_i_t;
          let x_i_t;
          let i_4;
          let i_5;
          if (locs_temp <= ecg_h.length) {
            const result = Find_max_locate(
              getRange(
                ecg_h,
                locs_temp - Math.round(0.15 * samplingRate),
                Math.round(0.15 * samplingRate),
              ),
            );
            y_i_t = result.y_i;
            i_4 = result.i_l;
            x_i_t = result.x_i;
          } else {
            const result = Find_max_locate(
              getRange(
                ecg_h,
                locs_temp - Math.round(0.15 * samplingRate),
                Math.round(0.15 * samplingRate),
              ),
            );
            y_i_t = result.y_i;
            i_5 = result.i_l;
            x_i_t = result.x_i;
          }
          if (y_i_t > THR_NOISE) {
            qrs_i_raw.push(locs_temp - Math.round(0.15 * samplingRate) + (x_i_t - 1));
            qrs_amp_raw.push(y_i_t);
            SIG_LEV1 = 0.25 * y_i_t + 0.75 * SIG_LEV1;
          }
          SIG_LEV = 0.25 * pks_temp + 0.75 * SIG_LEV;
        }
      }
    }

    if (pks[peak_index] >= THR_SIG) {
      skip = 0;
      if (qrs_c.length >= 3) {
        if (locs[peak_index] - qrs_i[qrs_i.length - 1] <= Math.round(0.36 * samplingRate)) {
          const Slope1_array = Diff(
            getRange(
              ecg_m,
              locs[peak_index] - Math.round(0.075 * samplingRate),
              Math.round(0.075 * samplingRate),
            ),
          );
          const Slope1 = getAverage(Slope1_array);
          const Slope2_array = Diff(
            getRange(
              ecg_m,
              qrs_i[qrs_i.length - 1] - Math.round(0.075 * samplingRate),
              Math.round(0.075 * samplingRate),
            ),
          );
          const Slope2 = getAverage(Slope2_array);
          if (Math.abs(Slope1) <= Math.abs(0.5 * Slope2)) {
            noise_c.push(pks[peak_index]);
            noise_i.push(locs[peak_index]);
            skip = 1;
            NOISE_LEV1 = 0.125 * y_i + 0.875 * NOISE_LEV1;
            NOISE_LEV = 0.125 * pks[peak_index] + 0.875 * NOISE_LEV;
          } else {
            skip = 0;
          }
        }
      }

      if (skip === 0) {
        qrs_c.push(pks[peak_index]);
        qrs_i.push(locs[peak_index]);
        if (y_i >= THR_SIG1) {
          if (ser_back === 1) {
            qrs_i_raw.push(x_i);
          } else {
            qrs_i_raw.push(locs[peak_index] - Math.round(0.15 * samplingRate) + (x_i - 1));
          }
        }
        qrs_amp_raw.push(y_i);
        SIG_LEV1 = 0.125 * y_i + 0.875 * SIG_LEV1;
      }

      SIG_LEV = 0.125 * pks[peak_index] + 0.875 * SIG_LEV;
    } else if (THR_NOISE <= pks[peak_index] && pks[peak_index] < THR_SIG) {
      NOISE_LEV1 = 0.125 * y_i + 0.875 * NOISE_LEV1;
      NOISE_LEV = 0.125 * pks[peak_index] + 0.875 * NOISE_LEV;
    } else if (pks[peak_index] < THR_NOISE) {
      noise_c.push(pks[peak_index]);
      noise_i.push(locs[peak_index]);
      NOISE_LEV1 = 0.125 * y_i + 0.875 * NOISE_LEV1;
      NOISE_LEV = 0.125 * pks[peak_index] + 0.875 * NOISE_LEV;
    }

    if (NOISE_LEV !== 0 || SIG_LEV !== 0) {
      THR_SIG = NOISE_LEV + 0.25 * Math.abs(SIG_LEV - NOISE_LEV);
      THR_NOISE = 0.5 * THR_SIG;
    }
    if (NOISE_LEV1 !== 0 || SIG_LEV1 !== 0) {
      THR_SIG1 = NOISE_LEV1 + 0.25 * Math.abs(SIG_LEV1 - THR_NOISE1);
      THR_NOISE1 = 0.5 * THR_SIG1;
    }
  }
  qrs_i = Remove_low_peak(qrs_i, ecg_m);
  return qrs_i;
};

/**
 * Applies a second-order zero-phase Butterworth low-pass to an ECG series. Returns null without data and the input unchanged at a zero cutoff, otherwise pads the series with two duplicated samples at each end, derives the coefficients from the pre-warped cutoff, runs the difference equation forward and then again in reverse, and copies the result back out at the original length.
 *
 * @param inData - ECG samples to filter.
 * @param SamplingRate - Samples per second of the recording.
 * @param CutOff - Low-pass cutoff in Hz; zero passes the samples through.
 * @returns The filtered samples, the input unchanged at a zero cutoff, or null when no data was supplied.
 */
export const Butterworth = (inData: number[], SamplingRate: number, CutOff: number) => {
  if (!inData) {
    return null;
  }

  if (CutOff === 0) {
    return inData;
  }

  const dF2 = inData.length - 1; // The data range is set with dF2
  const Dat2 = new Array(dF2 + 4); // Array with 4 extra points front and back
  const data = new Array(inData.length); // Ptr., changes passed data
  copyArray(inData, 0, data, 0, inData.length);

  copyArray(inData, 0, Dat2, 2, dF2);
  Dat2[0] = inData[0];
  Dat2[1] = Dat2[0];
  Dat2[dF2 + 2] = inData[dF2];
  Dat2[dF2 + 3] = Dat2[dF2 + 2];

  const wc = Math.tan((CutOff * Math.PI) / SamplingRate);
  const k1 = Math.SQRT2 * wc; // Sqrt(2) * wc
  const k2 = wc * wc;
  const a = k2 / (1 + k1 + k2);
  const b = 2 * a;
  const c = a;
  const k3 = b / k2;
  const d = -2 * a + k3;
  const e = 1 - 2 * a - k3;

  // RECURSIVE TRIGGERS - ENABLE filter is performed (first, last points constant)
  const DatYt = new Array(dF2 + 4);
  DatYt[0] = inData[0];
  DatYt[1] = DatYt[0];
  for (let s = 2; s < dF2 + 2; s += 1) {
    DatYt[s] =
      a * Dat2[s] + b * Dat2[s - 1] + c * Dat2[s - 2] + d * DatYt[s - 1] + e * DatYt[s - 2];
  }
  DatYt[dF2 + 2] = DatYt[dF2 + 1];
  DatYt[dF2 + 3] = DatYt[dF2 + 2];

  // FORWARD filter
  const DatZt = new Array(dF2 + 2);
  DatZt[dF2] = DatYt[dF2 + 2];
  DatZt[dF2 + 1] = DatYt[dF2 + 3];
  for (let t = -dF2 + 1; t <= 0; t += 1) {
    DatZt[-t] =
      a * DatYt[-t + 2] +
      b * DatYt[-t + 3] +
      c * DatYt[-t + 4] +
      d * DatZt[-t + 1] +
      e * DatZt[-t + 2];
  }

  copyArray(DatZt, 0, data, 0, dF2);
  return data;
};
